import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { RECORDING_PERIOD, RECORDING_PERIOD_VALUES, type RecordingPeriod } from '@autodialer/shared';

import { ApiError } from '../../api/client';
import {
  platformApi,
  type PlatformBalanceTransaction,
  type PlatformLiveCall,
  type PlatformRecordingSummary,
  type PlatformTenantDetail,
} from '../../api/platform';
import {
  Alert,
  Button,
  Card,
  CardGrid,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  FormField,
  Input,
  LoadingState,
  Metric,
  PageHeader,
  PageLayout,
  RowActions,
  Section,
  Select,
  StatusChip,
  Table,
} from '../../components';
import { useI18n, type TranslationKey } from '../../i18n';
import { useSession } from '../../auth/session-context';
import { formatDate, formatDateTime, formatMoney, formatSignedMoney } from '../../format';
import { formatDuration } from '../../telephony/CallPanel';
import { statusTon } from './PlatformTenantsPage';

/**
 * Kundendetailseite des Systemadmins (10_main_admin.md).
 *
 * Die ganze Seite ist darauf ausgelegt, dass ein Supportfall in einem Blick
 * zu beantworten ist: läuft das Konto, wie viel Guthaben, wie viel Nutzung,
 * Daten herunterladen, laufende Gespräche, zuhören, Aufnahmen holen, sperren.
 *
 * Was hier bewusst **fehlt**: eine Datensatzliste und eine Einzelansicht
 * eines Kontakts. Der Systemadmin bekommt die Kundendaten als Export, nicht
 * als Oberfläche zum Durchsuchen.
 */
export function PlatformTenantPage(): JSX.Element {
  const { t, tError } = useI18n();
  const { handleApiError } = useSession();
  const { tenantId = '' } = useParams();

  const [detail, setDetail] = useState<PlatformTenantDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sperren, setSperren] = useState(false);
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async () => {
    setError(null);
    try {
      setDetail(await platformApi.tenant(tenantId));
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    }
  }, [handleApiError, tenantId, tError]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const run = (aktion: () => Promise<unknown>): void => {
    void (async () => {
      setBusy(true);
      try {
        await aktion();
        await laden();
      } catch (caught) {
        handleApiError(caught);
        setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
      } finally {
        setBusy(false);
      }
    })();
  };

  if (error && !detail) {
    return (
      <PageLayout width="mittel">
        <ErrorState
          message={error}
          action={
            <Link className="btn btn-secondary" to="/systemadmin/kunden">
              {t('platform.tenant.back')}
            </Link>
          }
        />
      </PageLayout>
    );
  }

  if (!detail) {
    return (
      <PageLayout>
        <LoadingState />
      </PageLayout>
    );
  }

  const konto = detail.account;
  const gesperrt = konto.status === 'suspended';

  return (
    <PageLayout>
      <PageHeader
        title={konto.accountName ?? konto.customerNumber}
        description={konto.ownerEmail}
        actions={
          <>
            <Link className="btn btn-secondary" to="/systemadmin/kunden">
              {t('platform.tenant.back')}
            </Link>
            {gesperrt ? (
              <Button
                variant="primary"
                disabled={busy}
                onClick={() => run(() => platformApi.unsuspend(tenantId))}
              >
                {t('platform.tenant.unsuspend')}
              </Button>
            ) : (
              <Button variant="danger" disabled={busy} onClick={() => setSperren(true)}>
                {t('platform.tenant.suspend')}
              </Button>
            )}
          </>
        }
      />

      {error ? <Alert tone="error">{error}</Alert> : null}

      {/* --- Konto --- */}
      <Card>
        <Section
          title={t('platform.tenant.account')}
          actions={
            <StatusChip tone={statusTon(konto.status)}>
              {t(('platform.status.' + konto.status) as TranslationKey)}
            </StatusChip>
          }
        >
          <dl className="facts">
            <dt>{t('platform.tenant.customerNumber')}</dt>
            <dd>{konto.customerNumber}</dd>
            <dt>{t('platform.tenant.owner')}</dt>
            <dd>{konto.ownerEmail}</dd>
            <dt>{t('platform.tenants.registered')}</dt>
            <dd>{formatDate(konto.registeredAt)}</dd>
            <dt>{t('platform.tenants.balance')}</dt>
            <dd>{formatMoney(konto.balance)}</dd>
            <dt>{t('platform.tenants.price')}</dt>
            <dd>{formatMoney(konto.pricePerMinute, 3)}</dd>
            <dt>{t('platform.tenant.language')}</dt>
            <dd>{konto.locale.toUpperCase()}</dd>
          </dl>
        </Section>
      </Card>

      {/* --- Nutzung --- */}
      <Card>
        <Section title={t('platform.tenant.usage')}>
          <CardGrid>
            <Metric label={t('platform.tenant.usageMinutes')} value={String(detail.usage.totalMinutes)} />
            <Metric label={t('platform.tenant.usageToday')} value={String(detail.usage.minutesToday)} />
            <Metric
              label={t('platform.tenant.usageConnected')}
              value={String(detail.usage.connectedCalls)}
            />
            <Metric label={t('platform.tenant.usageAttempts')} value={String(detail.usage.attempts)} />
          </CardGrid>
          <p className="field-hint">
            {t('platform.tenant.usageLast')}:{' '}
            {detail.usage.lastCallAt ? formatDate(detail.usage.lastCallAt) : '–'}
          </p>
        </Section>
      </Card>

      {/* --- Kundendatenexport --- */}
      <Card>
        <Section title={t('platform.tenant.export')} description={t('platform.tenant.exportHint')}>
          <div className="row-actions">
            {/*
              Ein normaler Download mit Sitzungscookie - keine Adresse, die
              jemand ohne Anmeldung aufrufen könnte.
            */}
            <a className="btn btn-primary" href={platformApi.exportUrl(tenantId)}>
              {t('platform.tenant.exportAction')}
            </a>
          </div>
          <p className="field-hint">{t('platform.tenant.exportScope')}</p>
        </Section>
      </Card>

      <BalanceSection tenantId={tenantId} />
      <LiveCalls tenantId={tenantId} />
      <Recordings tenantId={tenantId} />

      {/* --- Kundeneinstellungen, lesend --- */}
      <Card>
        <Section title={t('platform.tenant.settings')} description={t('platform.tenant.settingsHint')}>
          <dl className="facts">
            <dt>{t('platform.tenants.campaigns')}</dt>
            <dd>{detail.settings.campaignCount}</dd>
            <dt>{t('platform.tenants.callers')}</dt>
            <dd>{detail.settings.callerCount}</dd>
            <dt>{t('platform.tenant.settingsCountries')}</dt>
            <dd>{detail.settings.defaultCountryCodes.join(', ') || '–'}</dd>
            <dt>{t('platform.tenant.settingsInbound')}</dt>
            <dd>{detail.settings.inboundCampaigns}</dd>
            <dt>{t('platform.tenant.settingsQueue')}</dt>
            <dd>{detail.settings.queueCampaigns}</dd>
            <dt>{t('platform.tenant.settingsRecording')}</dt>
            <dd>{detail.settings.recordingCampaigns}</dd>
          </dl>
        </Section>
      </Card>

      {/* --- Archiv --- */}
      <Card>
        <Section title={t('platform.tenant.archive')}>
          <Alert tone="info">{t('platform.tenant.archiveSoon')}</Alert>
        </Section>
      </Card>

      {sperren ? (
        <ConfirmDialog
          title={t('platform.tenant.suspendTitle')}
          message={t('platform.tenant.suspendText')}
          confirmLabel={t('platform.tenant.suspend')}
          busy={busy}
          onCancel={() => setSperren(false)}
          onConfirm={() => {
            setSperren(false);
            run(() => platformApi.suspend(tenantId));
          }}
        />
      ) : null}
    </PageLayout>
  );
}

/**
 * Guthabenbereich der Kundendetailseite (09_billing_payments.md).
 *
 * Bewusst keine Buchhaltungsoberflaeche: Saldo, zwei Knoepfe, ein Betrag, ein
 * Grund und die letzten Buchungen. Vor einer Abbuchung wird ausdruecklich
 * gewarnt, weil sie dem Kunden sofort Geld abzieht.
 *
 * Der Mindestaufladebetrag gilt hier **nicht** - er betrifft nur Aufladungen,
 * die ein Kunde selbst startet.
 */
function BalanceSection({ tenantId }: { tenantId: string }): JSX.Element {
  const { t, tError } = useI18n();
  const { handleApiError } = useSession();

  const [saldo, setSaldo] = useState<string | null>(null);
  const [buchungen, setBuchungen] = useState<PlatformBalanceTransaction[]>([]);
  const [betrag, setBetrag] = useState('');
  const [grund, setGrund] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [abzugFragen, setAbzugFragen] = useState(false);

  const laden = useCallback(async () => {
    try {
      const antwort = await platformApi.balance(tenantId);
      setSaldo(antwort.balance);
      setBuchungen(antwort.transactions);
    } catch (caught) {
      handleApiError(caught);
      setFehler(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    }
  }, [handleApiError, tenantId, tError]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const buchen = (aktion: () => Promise<unknown>): void => {
    void (async () => {
      setFehler(null);
      setBusy(true);
      try {
        await aktion();
        setBetrag('');
        setGrund('');
        await laden();
      } catch (caught) {
        handleApiError(caught);
        // Bei zu wenig Guthaben nennt der Server den verfuegbaren Betrag -
        // der steht dann in der Meldung.
        setFehler(
          caught instanceof ApiError ? tError(caught.code, caught.details) : tError(undefined),
        );
      } finally {
        setBusy(false);
      }
    })();
  };

  const eingabe = betrag.trim().replace(',', '.');
  const bereit = eingabe !== '' && !busy;

  return (
    <Card>
      <Section title={t('platform.tenant.balance')}>
        {fehler ? <Alert tone="error">{fehler}</Alert> : null}

        <p className="kennzahl-wert">{saldo === null ? '–' : formatMoney(saldo)}</p>

        <Field label={t('platform.tenant.balanceAmount')}>
          <Input type="text" value={betrag} onChange={setBetrag} placeholder="10.00" />
        </Field>
        <Field label={t('platform.tenant.balanceNote')}>
          <Input
            type="text"
            value={grund}
            onChange={setGrund}
            placeholder={t('platform.tenant.balanceNotePlaceholder')}
          />
        </Field>

        <div className="row-actions">
          <Button
            variant="primary"
            disabled={!bereit}
            onClick={() =>
              buchen(() => platformApi.creditBalance(tenantId, eingabe, grund.trim() || null))
            }
          >
            {t('platform.tenant.balanceAdd')}
          </Button>
          <Button variant="danger" disabled={!bereit} onClick={() => setAbzugFragen(true)}>
            {t('platform.tenant.balanceSubtract')}
          </Button>
        </div>

        <Section title={t('platform.tenant.balanceBookings')}>
          {buchungen.length === 0 ? (
            <EmptyState title={t('platform.tenant.balanceEmpty')} />
          ) : (
            <Table
              label={t('platform.tenant.balanceBookings')}
              headers={[
                t('balance.date'),
                t('balance.type'),
                t('balance.amountColumn'),
                t('balance.balanceAfter'),
                t('balance.note'),
              ]}
            >
              {buchungen.map((buchung) => (
                <tr key={buchung.id}>
                  <td>{formatDateTime(buchung.createdAt)}</td>
                  <td>{t(('balance.type.' + buchung.type) as TranslationKey)}</td>
                  <td>{formatSignedMoney(buchung.amount)}</td>
                  <td>{formatMoney(buchung.balanceAfter)}</td>
                  <td>
                    {buchung.visibleNote ?? '–'}
                    {buchung.internalNote ? (
                      <span className="field-hint">
                        {t('platform.tenant.balanceInternalNote')}: {buchung.internalNote}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </Section>
      </Section>

      {abzugFragen ? (
        <ConfirmDialog
          title={t('platform.tenant.balanceDebitTitle')}
          message={t('platform.tenant.balanceDebitText')}
          confirmLabel={t('platform.tenant.balanceDebitConfirm')}
          busy={busy}
          onCancel={() => setAbzugFragen(false)}
          onConfirm={() => {
            setAbzugFragen(false);
            buchen(() => platformApi.debitBalance(tenantId, eingabe, grund.trim() || null));
          }}
        >
          <dl className="facts">
            <dt>{t('platform.tenant.balanceAmount')}</dt>
            <dd>{formatMoney(eingabe)}</dd>
            <dt>{t('platform.tenant.balanceAvailable')}</dt>
            <dd>{saldo === null ? '–' : formatMoney(saldo)}</dd>
          </dl>
        </ConfirmDialog>
      ) : null}
    </Card>
  );
}

/** Wie oft die Live-Liste aktualisiert wird. */
const LIVE_INTERVALL_MS = 3_000;

/**
 * Laufende Gespräche mit `Zuhören`.
 *
 * Nur verbundene Gespräche - bei einer Anwahl, die noch klingelt, gibt es
 * nichts mitzuhören. Endet ein Gespräch, verschwindet es beim nächsten
 * Abgleich von selbst aus der Liste.
 */
function LiveCalls({ tenantId }: { tenantId: string }): JSX.Element {
  const { t, tError } = useI18n();
  const { handleApiError } = useSession();

  const [calls, setCalls] = useState<PlatformLiveCall[]>([]);
  const [hoert, setHoert] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [jetzt, setJetzt] = useState(() => Date.now());

  useEffect(() => {
    const holen = (): void => {
      void platformApi
        .liveCalls(tenantId)
        .then((antwort) => setCalls(antwort.calls))
        .catch((caught: unknown) => handleApiError(caught));
      void platformApi
        .listeningSession()
        .then((sitzung) => setHoert(sitzung.listening ? sitzung.callId : null))
        .catch(() => undefined);
      setJetzt(Date.now());
    };

    holen();
    const timer = window.setInterval(holen, LIVE_INTERVALL_MS);
    return () => window.clearInterval(timer);
  }, [handleApiError, tenantId]);

  const umschalten = (callId: string): void => {
    void (async () => {
      setError(null);
      try {
        if (hoert === callId) {
          await platformApi.stopListening(callId);
          setHoert(null);
        } else {
          await platformApi.listen(callId);
          setHoert(callId);
        }
      } catch (caught) {
        handleApiError(caught);
        setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
      }
    })();
  };

  return (
    <Card>
      <Section title={t('platform.live.title')} description={t('platform.live.hint')}>
        {error ? <Alert tone="error">{error}</Alert> : null}
        {hoert ? <Alert tone="info">{t('platform.live.silent')}</Alert> : null}

        {calls.length === 0 ? (
          <EmptyState title={t('platform.live.empty')} />
        ) : (
          <Table
            label={t('platform.live.title')}
            headers={[
              t('platform.live.campaign'),
              t('platform.live.caller'),
              t('platform.live.customer'),
              t('platform.live.duration'),
              '',
            ]}
          >
            {calls.map((call) => (
              <tr key={call.callId}>
                <td>{call.campaignName}</td>
                <td>{call.callerName ?? '–'}</td>
                <td>
                  {call.contactName ?? call.phoneNumber}
                  {call.contactName ? <div className="field-hint">{call.phoneNumber}</div> : null}
                </td>
                <td className="call-dauer">
                  {formatDuration(
                    Math.max(0, Math.floor((jetzt - new Date(call.connectedAt).getTime()) / 1000)),
                  )}
                </td>
                <RowActions>
                  {hoert === call.callId ? (
                    <Button variant="secondary" onClick={() => umschalten(call.callId)}>
                      {t('platform.live.stopListening')}
                    </Button>
                  ) : (
                    <Button variant="ghost" onClick={() => umschalten(call.callId)}>
                      {t('platform.live.listen')}
                    </Button>
                  )}
                </RowActions>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </Card>
  );
}

/**
 * Aufnahmen je Kampagne mit Zeitraumfilter.
 *
 * Die Aufnahmefunktion selbst entsteht später; bis dahin steht hier überall
 * eine Null. Das ist das richtige Ergebnis und kein Platzhalter.
 */
function Recordings({ tenantId }: { tenantId: string }): JSX.Element {
  const { t } = useI18n();
  const { handleApiError } = useSession();

  const [campaigns, setCampaigns] = useState<PlatformRecordingSummary[] | null>(null);
  const [period, setPeriod] = useState<RecordingPeriod>(RECORDING_PERIOD.ALL);

  useEffect(() => {
    void platformApi
      .recordings(tenantId)
      .then((antwort) => setCampaigns(antwort.campaigns))
      .catch((caught: unknown) => {
        handleApiError(caught);
        setCampaigns([]);
      });
  }, [handleApiError, tenantId]);

  const vorhanden = (campaigns ?? []).reduce((summe, eintrag) => summe + eintrag.count, 0);

  return (
    <Card>
      <Section title={t('platform.recordings.title')} description={t('platform.recordings.hint')}>
        {vorhanden === 0 ? <Alert tone="info">{t('platform.recordings.notYet')}</Alert> : null}

        <FormField label={t('platform.period.label')} help={t('platform.period.help')}>
          <Select
            value={period}
            options={RECORDING_PERIOD_VALUES.map((wert) => ({
              value: wert as RecordingPeriod,
              label: t(('platform.period.' + wert) as TranslationKey),
            }))}
            onChange={setPeriod}
          />
        </FormField>

        {campaigns === null ? <LoadingState /> : null}

        {campaigns !== null && campaigns.length === 0 ? (
          <EmptyState title={t('platform.recordings.empty')} />
        ) : null}

        {campaigns !== null && campaigns.length > 0 ? (
          <Table
            label={t('platform.recordings.title')}
            headers={[
              t('platform.recordings.campaign'),
              t('platform.recordings.count'),
              t('platform.recordings.range'),
              '',
            ]}
          >
            {campaigns.map((eintrag) => (
              <tr key={eintrag.campaignId}>
                <td>{eintrag.campaignName}</td>
                <td>{eintrag.count}</td>
                <td>
                  {eintrag.oldestAt && eintrag.newestAt
                    ? formatDate(eintrag.oldestAt) + ' – ' + formatDate(eintrag.newestAt)
                    : t('platform.recordings.none')}
                </td>
                <RowActions>
                  <a
                    className={eintrag.count === 0 ? 'btn btn-secondary' : 'btn btn-primary'}
                    href={platformApi.recordingExportUrl(tenantId, eintrag.campaignId, period)}
                  >
                    {t('platform.recordings.download')}
                  </a>
                </RowActions>
              </tr>
            ))}
          </Table>
        ) : null}
      </Section>
    </Card>
  );
}
